import { FC, useEffect, useState, useMemo } from "react";
import { Stack, Typography, Box, Skeleton } from "@mui/material";
import {
  CardContainer,
  LinkTextBox,
  ObjectIconBox,
  ObjectInfoRow,
  ObjectMovementStatusType,
  RowText,
  StatusBadge,
  StatusDot
} from "./ObjectListingCard.styled";
import { Link } from "react-router-dom";
import {
  DriverIcon,
  HashIcon,
  LinkArrowIcon,
  PlateNoIcon,
  SpeedIcon,
  StartLocation
} from "assets/icons";
import { useAppSelector } from "hooks";
import { selectSpecificObjectState } from "app/data/selectors";
import { Badge, utils } from "@vilocnv/allsetra-core";
import { modifyIconUrl } from "app/data/constants";
import { useTranslation } from "react-i18next";
import { HiddenBadgeIcon } from "components/ui/icons";
import InactiveObjectBadge from "components/ui/InactiveObjectBadge";
import { isInactiveObject } from "app/data/helpers";
import { round } from "lodash";

export interface ObjectDetailsCardProps {
  object: any;
  onObjectCardClick: any;
  resolvedAddresses?: any;
  resolvedAddressLoading?: any;
}

const ObjectListingCard: FC<ObjectDetailsCardProps> = ({
  object,
  onObjectCardClick,
  resolvedAddresses,
  resolvedAddressLoading
}) => {
  const { t } = useTranslation(["translation", "tableHeadingsTranslation"]);
  const [plateNumber, setPlateNumber] = useState<string>();
  const { activeObjectId } = useAppSelector(selectSpecificObjectState);
  const objectStatusesByUniqueId = useAppSelector(
    (state) => state.rootReducer.objectsReducer.objectStatusesByUniqueId
  );
  const inactive = isInactiveObject(
    object?.status ?? objectStatusesByUniqueId?.[object?.uniqueId]
  );
  const iconSrc = modifyIconUrl(object?.icon?.url);

  // Validate if location should be displayed
  const locationDisplayStatus = useMemo(() => {
    // Check if location is null or undefined
    if (!object?.location) {
      return { show: false, type: "hidden" };
    }

    const { latitude, longitude } = object.location;

    // Check if coordinates are null
    if (latitude === null || longitude === null) {
      return { show: false, type: "hidden" };
    }

    // Check if coordinates are 0
    if (latitude === 0 || longitude === 0) {
      return { show: false, type: "unknown" };
    }

    // Location is valid
    return { show: true, type: "visible" };
  }, [object?.location]);

  const getPlateNumber = () => {
    for (let i = 0; i < object?.metadata.length; i++) {
      if (object?.metadata[i]?.field?.label.toLowerCase() === "license plate") {
        setPlateNumber(
          `${object?.metadata[i]?.value || ""} ${object?.metadata[i]?.field.valueUnit || ""} `
        );
        break;
      }
    }
  };

  useEffect(() => {
    getPlateNumber();
  }, []);

  const speedInfo = useMemo(() => {
    const metadata = Array.isArray(object?.metadata) ? object.metadata : [];
    const speedMeta = metadata.find(
      (item: any) => item?.field?.label?.trim()?.toLowerCase() === "speed"
    );

    if (
      typeof object?.lastReportedSpeed === "number" &&
      Number.isFinite(object.lastReportedSpeed)
    ) {
      return {
        value: round(object.lastReportedSpeed, 1),
        unit: speedMeta?.field?.valueUnit?.trim() || "km/h"
      };
    }

    if (speedMeta?.value !== undefined && speedMeta?.value !== null) {
      const parsed = parseFloat(String(speedMeta.value).replace(",", "."));
      if (Number.isFinite(parsed)) {
        return {
          value: round(parsed, 1),
          unit: speedMeta?.field?.valueUnit?.trim() || "km/h"
        };
      }
    }

    return {
      value: 0,
      unit: "km/h"
    };
  }, [object?.lastReportedSpeed, object?.metadata]);

  const currentDriver = useMemo(() => {
    if (
      typeof object?.lastReportedDriver === "string" &&
      object.lastReportedDriver.trim()
    ) {
      return object.lastReportedDriver.trim();
    }

    const metadata = Array.isArray(object?.metadata) ? object.metadata : [];
    const driverMeta = metadata.find((item: any) => {
      const label = item?.field?.label?.trim()?.toLowerCase();
      return label === "chauffeur" || label === "driver";
    });

    if (
      typeof driverMeta?.value === "string" &&
      driverMeta.value.trim().length > 0
    ) {
      return driverMeta.value.trim();
    }

    if (typeof object?.driverName === "string" && object.driverName.trim()) {
      return object.driverName.trim();
    }

    if (object?.driver) {
      const composed =
        `${object.driver?.firstName ?? ""} ${object.driver?.lastName ?? ""}`.trim();
      if (composed) return composed;
    }

    return undefined;
  }, [
    object?.lastReportedDriver,
    object?.metadata,
    object?.driverName,
    object?.driver
  ]);

  const movementStatus = useMemo((): {
    type: ObjectMovementStatusType;
    label: string;
  } => {
    if (object?.isMoving === true || speedInfo.value > 0) {
      return {
        type: "moving",
        label: t("reports.moving", { defaultValue: "Moving" })
      };
    }

    const metadata = Array.isArray(object?.metadata) ? object.metadata : [];
    const ignitionMeta = metadata.find((item: any) => {
      const label = item?.field?.label?.trim()?.toLowerCase();
      return label === "ignition" || label === "enginerunning";
    });
    const ignitionValue = String(ignitionMeta?.value ?? "")
      .trim()
      .toLowerCase();
    const isIgnitionOn = ignitionValue === "true" || ignitionValue === "on";

    if (isIgnitionOn) {
      return {
        type: "idling",
        label: t("common.idling", { defaultValue: "Idling" })
      };
    }

    return {
      type: "stopped",
      label: t("common.stopped", { defaultValue: "Stopped" })
    };
  }, [object?.isMoving, object?.metadata, speedInfo.value, t]);

  const renderLocationAddress = () => {
    // If location should not be shown
    if (!locationDisplayStatus.show) {
      if (locationDisplayStatus.type === "hidden") {
        return (
          <Badge
            colorScheme="blank"
            icon={<HiddenBadgeIcon />}
            textWeight="light"
          >
            {t("typography.addressIsHidden")}
          </Badge>
        );
      } else {
        // type === "unknown"
        return (
          <Badge colorScheme="blank" textWeight="light">
            {t("typography.addressUnknown")}
          </Badge>
        );
      }
    }

    // Location is valid, show address
    if (object?.location?.resolvedAddress) {
      return object.location.resolvedAddress;
    }

    if (resolvedAddressLoading) {
      return <Skeleton variant="text" width={100} height={24} />;
    }

    // Try to find resolved address with more flexible comparison (6 decimal precision)
    const resolvedAddress = resolvedAddresses?.find((address: any) => {
      const latMatch =
        Math.abs(address.latitude - object?.location?.latitude) < 0.000001;
      const lngMatch =
        Math.abs(address.longitude - object?.location?.longitude) < 0.000001;
      return latMatch && lngMatch;
    })?.address;

    return resolvedAddress || "N/A";
  };

  const iconUrl = modifyIconUrl(
    object?.icon?.url || object?.objectType?.icon?.url
  );

  return (
    <CardContainer
      isActive={activeObjectId === object?.uniqueId}
      inactive={inactive}
      onClick={() => onObjectCardClick(object.uniqueId)}
      data-testid={`object-listing-card-${object?.uniqueId}`}
      sx={
        inactive
          ? {
              // Flush Inactive badge to the top edge (Figma); keep other sides.
              paddingTop: 0,
              overflow: "visible"
            }
          : undefined
      }
    >
      {inactive ? (
        <InactiveObjectBadge variant="hanging" left={53} top={0} />
      ) : null}
      <Stack direction={"column"}>
        <Stack
          direction={"row"}
          justifyContent={"space-between"}
          alignItems={"center"}
          spacing={1}
        >
          <Stack
            direction={"row"}
            spacing={1}
            alignItems={"center"}
            sx={{ minWidth: 0, flex: 1 }}
          >
            <ObjectIconBox sx={inactive ? { opacity: 0.55 } : undefined}>
              <img
                style={{ width: "24px", height: "24px" }}
                src={iconUrl}
                alt={"object-icon"}
              />
            </ObjectIconBox>
            <LinkTextBox sx={{ minWidth: 0 }}>
              <Link
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                  cursor: "pointer"
                }}
                to={`/dashboard/objects/details/${object.uniqueId}`}
              >
                <Typography
                  sx={{
                    fontWeight: 600,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    color: inactive ? "#646D7B" : undefined
                  }}
                  className="link-text"
                >
                  {object?.name || "N/A"}
                </Typography>
                <LinkArrowIcon className="arrow-icon" />
              </Link>
            </LinkTextBox>
          </Stack>
          <StatusBadge
            status={movementStatus.type}
            data-testid="object-card-movement-status"
          >
            <StatusDot status={movementStatus.type} />
            <span>{movementStatus.label}</span>
          </StatusBadge>
        </Stack>

        <Box>
          <Typography
            sx={{ fontSize: "14px", color: "#323946", mt: 0.5 }}
            component="div"
          >
            {renderLocationAddress()}
          </Typography>
        </Box>
        <ObjectInfoRow>
          {currentDriver ? (
            <Box
              display={"flex"}
              flexDirection={"row"}
              alignItems={"center"}
              data-testid="object-card-driver"
            >
              <DriverIcon />
              <RowText>{currentDriver}</RowText>
            </Box>
          ) : null}
          <Box
            display={"flex"}
            flexDirection={"row"}
            alignItems={"center"}
            data-testid="object-card-speed"
          >
            <SpeedIcon />
            <RowText>{`${speedInfo.value} ${speedInfo.unit}`}</RowText>
          </Box>
        </ObjectInfoRow>
        <ObjectInfoRow>
          <Box display={"flex"} flexDirection={"row"} alignItems={"center"}>
            <StartLocation size={16} />
            <RowText>
              {utils.formatDate({
                date: object?.lastUpdated || object?.location?.date,
                whenEmpty: "-"
              })}
            </RowText>
          </Box>
        </ObjectInfoRow>
        <ObjectInfoRow>
          <Box display={"flex"} flexDirection={"row"} alignItems={"center"}>
            <HashIcon />
            <RowText>{object?.aNumber}</RowText>
          </Box>
          {plateNumber?.trim() ? (
            <Box display={"flex"} flexDirection={"row"} alignItems={"center"}>
              <PlateNoIcon />
              <RowText>{plateNumber}</RowText>
            </Box>
          ) : (
            <></>
          )}
          <Box display={"flex"} flexDirection={"row"} alignItems={"center"}>
            {object?.deviceTypeSimpleIconUrl && (
              <>
                <img
                  width="16"
                  height="16"
                  src={object?.deviceTypeSimpleIconUrl}
                  alt="device-type-icon"
                />
                <RowText>
                  {object?.deviceTypeSimpleIconUrl?.includes("location_pin")
                    ? t("deviceTypes.gps")
                    : t("deviceTypes.peilzender")}
                </RowText>
              </>
            )}
          </Box>
        </ObjectInfoRow>
      </Stack>
    </CardContainer>
  );
};

export default ObjectListingCard;
